import React, { useState, useEffect } from 'react';
import './SpeedRound.css';

/**
 * Speed Round Game - Answer as many questions as possible in 60 seconds
 * Gamification: Timer, score, streak bonuses, leaderboard
 */
const SpeedRound = ({ data }) => {
  const [gameState, setGameState] = useState('menu'); // menu, playing, finished
  const [timeLeft, setTimeLeft] = useState(60);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [stats, setStats] = useState({
    correct: 0,
    incorrect: 0,
    bestStreak: 0
  });

  // Load all questions
  useEffect(() => {
    if (data && data.subjects) {
      const allQuestions = data.subjects.flatMap(subject =>
        subject.topics.flatMap(topic => 
          topic.multiple_choice_questions.map(q => ({
            ...q,
            subject: subject.name,
            topic: topic.name
          }))
        )
      );
      // Shuffle questions
      const shuffled = allQuestions.sort(() => Math.random() - 0.5);
      setQuestions(shuffled);
    }
  }, [data]);

  // Timer countdown
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === 'playing') {
      endGame();
    }
  }, [timeLeft, gameState]);

  const startGame = () => {
    setGameState('playing');
    setTimeLeft(60);
    setScore(0);
    setStreak(0);
    setQuestionIndex(0);
    setStats({ correct: 0, incorrect: 0, bestStreak: 0 });
    loadNextQuestion();
  };

  const loadNextQuestion = () => {
    if (questionIndex < questions.length) {
      setCurrentQuestion(questions[questionIndex]);
      setSelectedAnswer(null);
      setShowFeedback(false);
    } else {
      endGame();
    }
  };

  const handleAnswer = (answer) => {
    if (showFeedback) return;

    setSelectedAnswer(answer);
    setShowFeedback(true);

    const isCorrect = answer === currentQuestion.answer;
    
    if (isCorrect) {
      const newStreak = streak + 1;
      const streakBonus = Math.floor(newStreak / 3) * 10; // Bonus every 3 correct
      const points = 10 + streakBonus;
      
      setScore(score + points);
      setStreak(newStreak);
      setStats(prev => ({
        ...prev,
        correct: prev.correct + 1,
        bestStreak: Math.max(prev.bestStreak, newStreak)
      }));
    } else {
      setStreak(0);
      setStats(prev => ({
        ...prev,
        incorrect: prev.incorrect + 1
      }));
    }

    // Auto-advance after 1.5 seconds
    setTimeout(() => {
      setQuestionIndex(questionIndex + 1);
      loadNextQuestion();
    }, 1500);
  };

  const endGame = () => {
    setGameState('finished');
    // Save high score to localStorage
    const highScores = JSON.parse(localStorage.getItem('mcatHighScores') || '[]');
    highScores.push({
      score,
      correct: stats.correct,
      date: new Date().toISOString(),
      streak: stats.bestStreak
    });
    highScores.sort((a, b) => b.score - a.score);
    localStorage.setItem('mcatHighScores', JSON.stringify(highScores.slice(0, 10)));
  };

  const getStreakMessage = () => {
    if (streak >= 10) return '🔥 ON FIRE! 🔥';
    if (streak >= 5) return '⚡ Hot Streak! ⚡';
    if (streak >= 3) return '✨ Nice! ✨';
    return '';
  };

  // Menu Screen
  if (gameState === 'menu') {
    const highScores = JSON.parse(localStorage.getItem('mcatHighScores') || '[]');
    
    return (
      <div className="speed-round-menu">
        <h1>⚡ Speed Round</h1>
        <p className="game-description">
          Answer as many questions as possible in 60 seconds!<br/>
          Build streaks for bonus points! 🔥
        </p>
        
        <div className="game-rules">
          <h3>Rules:</h3>
          <ul>
            <li>✓ Each correct answer: <strong>10 points</strong></li>
            <li>🔥 3-streak bonus: <strong>+10 points</strong></li>
            <li>🔥 6-streak bonus: <strong>+20 points</strong></li>
            <li>⚡ 60 seconds on the clock</li>
          </ul>
        </div>

        <button className="start-button" onClick={startGame}>
          Start Game
        </button>

        {highScores.length > 0 && (
          <div className="leaderboard">
            <h3>🏆 High Scores</h3>
            <ol>
              {highScores.slice(0, 5).map((entry, idx) => (
                <li key={idx}>
                  <span className="score">{entry.score} pts</span>
                  <span className="details">
                    ({entry.correct} correct, {entry.streak} best streak)
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  }

  // Game Playing Screen
  if (gameState === 'playing' && currentQuestion) {
    const isCorrect = selectedAnswer === currentQuestion.answer;
    
    return (
      <div className="speed-round-game">
        <div className="game-header">
          <div className="timer">
            <span className={timeLeft <= 10 ? 'timer-warning' : ''}>
              ⏱️ {timeLeft}s
            </span>
          </div>
          <div className="score-display">
            <span className="score">Score: {score}</span>
            {streak > 0 && (
              <span className="streak">
                🔥 {streak} streak
              </span>
            )}
          </div>
        </div>

        {streak >= 3 && (
          <div className="streak-message">
            {getStreakMessage()}
          </div>
        )}

        <div className="question-card">
          <div className="question-meta">
            <span className="subject-badge">{currentQuestion.subject}</span>
            <span className="topic-badge">{currentQuestion.topic}</span>
          </div>
          
          <p className="question-text">{currentQuestion.question}</p>
          
          <div className="options-grid">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(option)}
                disabled={showFeedback}
                className={`option-button ${
                  showFeedback && option === currentQuestion.answer ? 'correct' : ''
                } ${
                  showFeedback && selectedAnswer === option && option !== currentQuestion.answer ? 'incorrect' : ''
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {showFeedback && (
            <div className={`feedback ${isCorrect ? 'correct-feedback' : 'incorrect-feedback'}`}>
              {isCorrect ? (
                <div>
                  <h3>✓ Correct!</h3>
                  {streak > 0 && streak % 3 === 0 && (
                    <p className="bonus">+{Math.floor(streak / 3) * 10} Streak Bonus!</p>
                  )}
                </div>
              ) : (
                <div>
                  <h3>✗ Incorrect</h3>
                  <p>Correct answer: <strong>{currentQuestion.answer}</strong></p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(questionIndex / questions.length) * 100}%` }}
          />
        </div>
      </div>
    );
  }

  // Game Finished Screen
  if (gameState === 'finished') {
    const accuracy = stats.correct + stats.incorrect > 0 
      ? Math.round((stats.correct / (stats.correct + stats.incorrect)) * 100)
      : 0;

    return (
      <div className="speed-round-results">
        <h1>🎉 Game Over!</h1>
        
        <div className="final-score">
          <div className="score-big">{score}</div>
          <div className="score-label">Final Score</div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.correct}</div>
            <div className="stat-label">Correct</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.incorrect}</div>
            <div className="stat-label">Incorrect</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{accuracy}%</div>
            <div className="stat-label">Accuracy</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.bestStreak}</div>
            <div className="stat-label">Best Streak</div>
          </div>
        </div>

        <div className="performance-message">
          {score >= 200 && <p>🏆 Outstanding! MCAT Master!</p>}
          {score >= 150 && score < 200 && <p>⭐ Excellent work!</p>}
          {score >= 100 && score < 150 && <p>👍 Good job!</p>}
          {score < 100 && <p>💪 Keep practicing!</p>}
        </div>

        <div className="action-buttons">
          <button className="play-again-button" onClick={startGame}>
            Play Again
          </button>
          <button className="menu-button" onClick={() => setGameState('menu')}>
            Main Menu
          </button>
        </div>
      </div>
    );
  }

  return <div>Loading...</div>;
};

export default SpeedRound;
